import { Fragment, useState, type ReactNode } from "react";
import { BarChart3, Table2 } from "lucide-react";
import { cn, normalizeText } from "../../ui/utils";
import {
  AssistantTable,
  badgeMinWidth,
  numericMinWidth,
  typeMinWidth,
  wordMinWidth,
  type AssistantTableColumn,
} from "./AssistantTable";
import { ChartMessage } from "../charts";
import {
  detectColumnKinds,
  isTableSeparator,
  parseAlign,
  plainText,
  splitTableRow,
  tableToChartBlock,
  type ColumnKind,
} from "../lib/markdownTable";
import type { ChartBlock } from "../types";

// Applies to plain paragraphs only; this is the approved look, not a trial.
const JUSTIFY_PROSE = "text-justify [text-justify:inter-word]";

function isChartBlock(obj: unknown): obj is ChartBlock {
  if (!obj || typeof obj !== "object") return false;
  const b = obj as ChartBlock;
  if (b.type !== "chart") return false;
  // Rows must be non-null plain objects or normalizeChart crashes during render and blanks the app.
  if (Array.isArray(b.data)) {
    return b.data.every((d) => d != null && typeof d === "object" && !Array.isArray(d));
  }
  return typeof b.state === "string";
}

function parseChartBlock(src: string): ChartBlock | null {
  try {
    const obj = JSON.parse(src) as unknown;
    if (isChartBlock(obj)) return obj;
  } catch {}
  return null;
}

function renderFenced(lines: string[], lang: string, keyN: number): ReactNode {
  if (lang === "chart") {
    const chart = parseChartBlock(lines.join("\n"));
    if (chart) return <ChartMessage key={`chart-${keyN}`} spec={chart} />;
  }
  return (
    <pre
      key={`c-${keyN}`}
      className="scrollbar-neutral overflow-x-auto rounded-lg border border-border/60 bg-muted/50 p-3 text-[0.75rem] font-mono leading-relaxed text-foreground dark:border-white/10 dark:bg-white/[0.04]"
    >
      <code>{lines.join("\n")}</code>
    </pre>
  );
}

// Security: link schemes are allowlisted - model-generated text must never yield a javascript:/data: href.
function safeHref(raw: string): string | null {
  const url = raw.trim();
  const scheme = url.match(/^([a-z][a-z0-9+.-]*):/i);
  if (scheme) {
    const s = scheme[1].toLowerCase();
    if (s !== "http" && s !== "https" && s !== "mailto" && s !== "tel") return null;
  }
  return url;
}

function withInlineSeparators(text: string, key: number): ReactNode {
  if (!text.includes(" | ")) return <span key={key}>{text}</span>;
  const segments = text.split(" | ");
  return (
    <span key={key}>
      {segments.map((segment, s) => (
        <Fragment key={s}>
          {s > 0 && (
            <span aria-hidden className="mx-1.5 font-normal text-muted-foreground/50">
              |
            </span>
          )}
          {segment}
        </Fragment>
      ))}
    </span>
  );
}

function renderInline(text: string): ReactNode[] {
  const tokenRe = /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|\[[^\]\n]+]\([^)\s]+\))/g;
  const parts = normalizeText(text).split(tokenRe);
  return parts.map((part, i) => {
    if (!part) return null;
    if (/^`[^`]+`$/.test(part)) {
      return (
        <code
          key={i}
          className="rounded-md border border-border/50 bg-muted/70 px-1.5 py-0.5 font-mono text-[0.6875rem] text-foreground dark:border-white/10 dark:bg-white/[0.06]"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (/^\*[^*]+\*$/.test(part)) {
      return (
        <em key={i} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }
    const link = part.match(/^\[([^\]]+)]\(([^)\s]+)\)$/);
    if (link) {
      const href = safeHref(link[2]);
      if (!href) {
        return <span key={i}>{link[1]}</span>;
      }
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline-offset-2 hover:underline"
        >
          {link[1]}
        </a>
      );
    }
    return withInlineSeparators(part, i);
  });
}

function RankBadge({ value }: { value: string }) {
  return (
    <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-md bg-primary/10 px-1 text-[0.6875rem] font-semibold tabular-nums text-primary dark:bg-primary/15">
      {value}
    </span>
  );
}

const STATUS_TONES: Array<[RegExp, { chip: string }]> = [
  [
    /^(active|enabled|passing|pass(ed)?|compliant|complete(d)?|done|approved|resolved|healthy|ready|published|live|success|ok|met)$/i,
    { chip: "border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" },
  ],
  [
    /^(fail(ed|ing)?|non[-\s]?compliant|rejected|error|critical|blocked|overdue|expired|breach(ed)?|not met)$/i,
    { chip: "border-red-500/25 bg-red-500/10 text-red-600 dark:text-red-300" },
  ],
  [
    /^(high)$/i,
    { chip: "border-orange-500/25 bg-orange-500/10 text-orange-600 dark:text-orange-300" },
  ],
  [
    /^(draft|pending|in[-\s]?progress|review|reviewing|warning|partial|medium|at[-\s]?risk|waiting|queued|scheduled)$/i,
    { chip: "border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-300" },
  ],
  [
    /^(info|new|low|note|planned)$/i,
    { chip: "border-indigo-500/25 bg-indigo-500/10 text-indigo-600 dark:text-indigo-300" },
  ],
  [
    /^(inactive|disabled|archived|closed|deprecated|n\/?a|none|unknown|not applicable)$/i,
    { chip: "border-slate-400/25 bg-slate-400/10 text-slate-500 dark:text-slate-300" },
  ],
];

function StatusPill({ value }: { value: string }) {
  const v = value.trim();
  if (!v) return null;
  const tone = STATUS_TONES.find(([re]) => re.test(v))?.[1] ?? {
    chip: "border-border bg-muted/60 text-muted-foreground",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[0.6875rem] font-medium",
        tone.chip,
      )}
    >
      {v}
    </span>
  );
}

const TYPE_TINTS = [
  "border-violet-500/25 bg-violet-500/10 text-violet-600 dark:text-violet-300",
  "border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
  "border-sky-500/25 bg-sky-500/10 text-sky-600 dark:text-sky-300",
  "border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-300",
  "border-pink-500/25 bg-pink-500/10 text-pink-600 dark:text-pink-300",
  "border-cyan-500/25 bg-cyan-500/10 text-cyan-600 dark:text-cyan-300",
];

function typeTint(value: string): string {
  let h = 0;
  for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) >>> 0;
  return TYPE_TINTS[h % TYPE_TINTS.length];
}

function TypePill({ value }: { value: string }) {
  const v = value.trim();
  if (!v) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-[0.6875rem] font-medium",
        typeTint(v.toLowerCase()),
      )}
    >
      {v}
    </span>
  );
}

function renderCell(raw: string, kind: ColumnKind): ReactNode {
  if (kind === "rank") {
    const v = plainText(raw).trim();
    return v ? <RankBadge value={v} /> : null;
  }
  if (kind === "status") return <StatusPill value={plainText(raw)} />;
  if (kind === "type") return <TypePill value={plainText(raw)} />;
  return renderInline(raw);
}

function MarkdownTable({
  header,
  aligns,
  rows,
}: {
  header: string[];
  aligns: ("left" | "right" | "center")[];
  rows: string[][];
}) {
  const colCount = Math.max(header.length, ...rows.map((r) => r.length), 1);
  const kinds = detectColumnKinds(header, rows, colCount);

  const maxLen = (i: number): number => {
    let m = plainText(header[i] ?? "").length;
    for (const row of rows) m = Math.max(m, plainText(row[i] ?? "").length);
    return m;
  };

  const longestWordLen = (i: number): number => {
    let m = 0;
    const texts = [header[i] ?? "", ...rows.map((r) => r[i] ?? "")];
    for (const t of texts) {
      for (const token of plainText(t).split(/[\s-]+/)) m = Math.max(m, token.length);
    }
    return m;
  };

  const isNumericCol = (i: number): boolean => {
    const vals = rows.map((r) => plainText(r[i] ?? "").trim()).filter((v) => v.length > 0);
    return vals.length > 0 && vals.every((v) => /^[$£€]?\s?\d[\d.,%+\-\s/x]*$/i.test(v));
  };

  let titleIdx = -1;
  let titleLen = -1;
  for (let i = 0; i < colCount; i++) {
    if (kinds[i] === "text" && !isNumericCol(i)) {
      const len = maxLen(i);
      if (len > titleLen) {
        titleLen = len;
        titleIdx = i;
      }
    }
  }
  if (titleIdx < 0) titleIdx = kinds.findIndex((k) => k !== "rank");
  if (titleIdx < 0) titleIdx = 0;

  const columns: AssistantTableColumn[] = Array.from({ length: colCount }, (_, i) => {
    const kind = kinds[i];
    if (kind === "rank") return { align: "center", noWrap: true, minPx: 44, grow: 0 };
    if (kind === "status") {
      return { align: "left", noWrap: true, minPx: badgeMinWidth(maxLen(i)), grow: 0 };
    }
    if (kind === "type") {
      return { align: "left", noWrap: true, minPx: typeMinWidth(maxLen(i)), grow: 0 };
    }
    if (isNumericCol(i)) {
      return { align: "center", noWrap: true, minPx: numericMinWidth(maxLen(i)), grow: 0 };
    }
    const isTitle = i === titleIdx;
    const grow = isTitle ? 5 : maxLen(i) >= 18 ? 2 : 1;
    return { align: aligns[i] ?? "left", grow, isTitle, minPx: wordMinWidth(longestWordLen(i)) };
  });
  const headerCells = Array.from({ length: colCount }, (_, i) => renderInline(header[i] ?? ""));
  const bodyRows = rows.map((row) =>
    Array.from({ length: colCount }, (_, c) => renderCell(row[c] ?? "", kinds[c])),
  );

  return <AssistantTable columns={columns} header={headerCells} rows={bodyRows} />;
}

function ViewToggleButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[0.6875rem] font-medium transition-colors",
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function TableChartView({
  header,
  aligns,
  rows,
  chart,
}: {
  header: string[];
  aligns: ("left" | "right" | "center")[];
  rows: string[][];
  chart: ChartBlock;
}) {
  const [view, setView] = useState<"chart" | "table">("chart");
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-end gap-1">
        <ViewToggleButton
          active={view === "chart"}
          onClick={() => setView("chart")}
          icon={<BarChart3 className="size-3.5" />}
          label="Chart"
        />
        <ViewToggleButton
          active={view === "table"}
          onClick={() => setView("table")}
          icon={<Table2 className="size-3.5" />}
          label="Table"
        />
      </div>
      {view === "chart" ? (
        // Flagged as table-derived: these numbers were parsed out of the assistant's own markdown
        // table, not built by a platform chart mapper off a DTO. It is a convenience rendering of
        // what is written above, and must not read as a reproduction of a dashboard visualization.
        <ChartMessage spec={chart} derivedFromTable />
      ) : (
        <MarkdownTable header={header} aligns={aligns} rows={rows} />
      )}
    </div>
  );
}

export function ChatMarkdown({ content }: { content: string }) {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const out: ReactNode[] = [];
  let i = 0;
  let para: string[] = [];
  let list: { kind: "ul" | "ol"; items: string[] } | null = null;
  /** Ordered items emitted since the last structural break — see flushList / resetOrdinal. */
  let olSeq = 0;
  let code: string[] | null = null;
  let codeLang = "";

  const flushPara = () => {
    if (!para.length) return;
    out.push(
      <p
        key={`p-${out.length}`}
        className={cn("text-xs leading-relaxed text-foreground", JUSTIFY_PROSE)}
      >
        {renderInline(para.join(" "))}
      </p>,
    );
    para = [];
  };

  const flushList = () => {
    if (!list) return;
    const baseCls = "space-y-1.5 pl-5 text-xs leading-relaxed text-foreground marker:text-muted-foreground";
    if (list.kind === "ul") {
      out.push(
        <ul key={`l-${out.length}`} className={`${baseCls} list-disc marker:text-primary/60`}>
          {list.items.map((it, idx) => (
            <li key={idx} className="pl-0.5">
              {renderInline(it)}
            </li>
          ))}
        </ul>,
      );
    } else {
      // Continue the sequence rather than restarting it. The parser is flat, so anything between
      // two numbered items — sub-bullets, a paragraph, a blank line — closes the <ol> and opens a
      // new one. Numbering from CSS then restarts at 1 on every item that carries sub-content,
      // which is exactly when a numbered list matters most. `olSeq` survives those interruptions
      // and only resets at a structural break (see resetOrdinal).
      const start = olSeq + 1;
      olSeq += list.items.length;
      out.push(
        <ol
          key={`l-${out.length}`}
          start={start}
          className={`${baseCls} list-decimal marker:font-semibold`}
        >
          {list.items.map((it, idx) => (
            <li key={idx} className="pl-0.5">
              {renderInline(it)}
            </li>
          ))}
        </ol>,
      );
    }
    list = null;
  };

  /**
   * Ends the current run of ordered items.
   *
   * Called only where a *structural* break is emitted — heading, rule, code fence, table. A `ul`,
   * a paragraph or a blank line interrupts a list without ending it, so those deliberately do not
   * reset. Two genuinely separate numbered lists split by nothing but a paragraph would therefore
   * keep counting; that is rarer than the bug this fixes, and a heading resets it.
   */
  const resetOrdinal = () => {
    olSeq = 0;
  };

  while (i < lines.length) {
    const line = lines[i];

    if (code !== null) {
      if (/^```/.test(line.trim())) {
        out.push(renderFenced(code, codeLang, out.length));
        code = null;
        codeLang = "";
      } else {
        code.push(line);
      }
      i++;
      continue;
    }
    if (/^```/.test(line.trim())) {
      flushPara();
      flushList();
      resetOrdinal();
      code = [];
      codeLang = line.trim().replace(/^`+/, "").trim().toLowerCase();
      i++;
      continue;
    }

    if (
      /^\s*\|.*\|\s*$/.test(line) &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      flushPara();
      flushList();
      resetOrdinal();
      const header = splitTableRow(line);
      const aligns = splitTableRow(lines[i + 1]).map(parseAlign);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      const chart = tableToChartBlock(header, rows);
      out.push(
        chart ? (
          <TableChartView
            key={`t-${out.length}`}
            header={header}
            aligns={aligns}
            rows={rows}
            chart={chart}
          />
        ) : (
          <MarkdownTable
            key={`t-${out.length}`}
            header={header}
            aligns={aligns}
            rows={rows}
          />
        ),
      );
      continue;
    }

    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      flushPara();
      flushList();
      resetOrdinal();
      out.push(
        <hr
          key={`hr-${out.length}`}
          className="border-0 border-t border-border/40"
        />,
      );
      i++;
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      flushPara();
      flushList();
      resetOrdinal();
      const level = heading[1].length;
      const text = heading[2];
      if (level === 1) {
        out.push(
          <h3
            key={`h-${out.length}`}
            className="pt-0.5 text-[0.9375rem] font-bold leading-snug tracking-tight text-foreground"
          >
            {renderInline(text)}
          </h3>,
        );
      } else if (level === 2) {
        out.push(
          <h4
            key={`h-${out.length}`}
            className="text-[0.8125rem] font-semibold leading-snug text-foreground"
          >
            {renderInline(text)}
          </h4>,
        );
      } else {
        out.push(
          <h5
            key={`h-${out.length}`}
            className="text-[0.6875rem] font-semibold tracking-wide text-muted-foreground"
          >
            {renderInline(text)}
          </h5>,
        );
      }
      i++;
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      flushPara();
      flushList();
      resetOrdinal();
      const quote: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quote.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      out.push(
        <blockquote
          key={`q-${out.length}`}
          className="rounded-r-lg border-l-2 border-primary/50 bg-primary/[0.04] px-3 py-2 text-xs leading-relaxed text-foreground/90 dark:bg-white/[0.03]"
        >
          {renderInline(quote.join(" ").trim())}
        </blockquote>,
      );
      continue;
    }

    const ul = line.match(/^\s*[-*+]\s+(.*)$/);
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    if (ul) {
      flushPara();
      if (!list || list.kind !== "ul") {
        flushList();
        list = { kind: "ul", items: [] };
      }
      list.items.push(ul[1]);
      i++;
      continue;
    }
    if (ol) {
      flushPara();
      if (!list || list.kind !== "ol") {
        flushList();
        list = { kind: "ol", items: [] };
      }
      list.items.push(ol[1]);
      i++;
      continue;
    }

    if (!line.trim()) {
      flushPara();
      // A blank line between items does not end a list. In markdown it makes the list *loose* —
      // the items still belong to one list, they just get more air. Closing it here started a new
      // <ol> for every paragraph-separated item, so each one restarted at "1." and an ordered list
      // stopped being ordered. Look past the blank run: if the next content line is another item of
      // the same kind, the list continues.
      if (list) {
        let next = i + 1;
        while (next < lines.length && !lines[next].trim()) next++;
        const continues =
          next < lines.length
          && (list.kind === "ul"
            ? /^\s*[-*+]\s+/.test(lines[next])
            : /^\s*\d+\.\s+/.test(lines[next]));
        if (continues) {
          i = next;
          continue;
        }
      }
      flushList();
      i++;
      continue;
    }

    flushList();
    para.push(line);
    i++;
  }

  if (code !== null) {
    out.push(renderFenced(code, codeLang, out.length));
  }
  flushPara();
  flushList();

  return <div className="space-y-3">{out}</div>;
}
